import {useEffect, useState} from 'react';

import type {TestIdValue} from '../types';
import {logger} from '../logger';
import {config} from '../config';
import {CALL_TYPE, MEETING_MANAGER_PATH, SHADES} from '../constants';
import {setApplicationConfig} from '../applicationConfig';
import {transformHiddenFunctionalityArrayToObject} from '../utils/transformHiddenFunctionalityArrayToObject';
import {parseLegacyParams} from '../utils/parseLegacyParams';
import {parseMeetingEntryPath, router} from '../router';
import {toClientCallType} from '../utils/toClientCallTypes';

import type {Favicon, Manifest} from './types';
import {getBrandingPath, DEFAULT, loadBranding} from './loadBranding';
import {getClientCallType} from '../utils/getClientCallType';
import {setLanguages} from './setLanguages';
export {setLanguages} from './setLanguages';

const setBrandApplicationConfig = (
    brandingApplicationConfig: Manifest['applicationConfig'] = {},
) => {
    for (const [key, value] of Object.entries(brandingApplicationConfig)) {
        setApplicationConfig(
            key as keyof Manifest['applicationConfig'],
            key === 'hiddenFunctionality'
                ? transformHiddenFunctionalityArrayToObject(
                      value as TestIdValue[],
                  )
                : value,
        );
    }
};

const setBrandDefaultUserConfig = (
    defaultUserConfig: Manifest['defaultUserConfig'] = {},
) => {
    for (const [k, value] of Object.entries(defaultUserConfig)) {
        const key = k as keyof Manifest['defaultUserConfig'];
        // Apply only for unchanged defaults
        if (config.isDefaultValue(key)) {
            if (key === 'callType' && typeof value === 'string') {
                config.set({key, value: getClientCallType(value) ?? CALL_TYPE});
                continue;
            }
            config.set({key, value});
        }
    }
};

const setBrandPalette = (colorPalette: Manifest['colorPalette']) => {
    if (colorPalette && colorPalette.length > 0) {
        if (colorPalette.length !== SHADES.length) {
            logger.warn(
                'color palette has too many/few elements. check your branding manifest version.',
            );
        }
        colorPalette.forEach((color, idx) => {
            document.documentElement.style.setProperty(
                `--color-brand-${SHADES[idx]}`,
                color,
            );
        });
    } else {
        for (const shade of SHADES) {
            document.documentElement.style.removeProperty(
                `--color-brand-${shade}`,
            );
        }
    }
};

const setBrandBgColor = (backgroundColor: Manifest['backgroundColor']) => {
    if (backgroundColor) {
        document.documentElement.style.setProperty(
            '--color-brand-background',
            backgroundColor,
        );
    } else {
        document.documentElement.style.removeProperty(
            '--color-brand-background',
        );
    }
};

const setBrandApplicationTitle = (appTitle: Manifest['appTitle']) => {
    if (appTitle) {
        document.title = appTitle;
    }
};

const setBgReplacementAssets = (
    bgImageAssets: Manifest['applicationConfig']['bgImageAssets'],
    backgroundImageUrl: Manifest['defaultUserConfig']['bgImageUrl'],
) => {
    // Normalization of the old brands, makes sure we always have bgImageAssets if bgImageUrl is specified
    if (backgroundImageUrl && !bgImageAssets) {
        setApplicationConfig('bgImageAssets', [backgroundImageUrl]);
    }
};

const setFavicon = (favicon?: Favicon) => {
    if (!favicon || !('href' in favicon)) {
        return;
    }

    // Remove all existing favicon and apple-touch-icon links
    document
        .querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"]')
        .forEach(el => {
            el.remove();
        });

    const link = document.createElement('link');
    link.rel = 'icon';
    if (favicon.type) {
        link.type = favicon.type;
    }
    if (favicon.sizes) {
        link.sizes.add(favicon.sizes);
    }
    link.href = getBrandingPath(favicon.href);
    document.head.appendChild(link);
};

const normalizePlugins = (brand: Manifest) => {
    if (!brand.plugins) {
        return;
    }
    const plugins: NonNullable<Manifest['plugins']> = [];
    for (const plugin of brand.plugins) {
        if (!plugin.src) {
            logger.error({plugin}, 'A Plugin must have a "src"');
            continue;
        }
        if (plugins.find(p => plugin.id && plugin.id === p.id)) {
            logger.error(
                {plugin},
                'Plugin with duplicate id found in branding manifest.',
            );
            continue;
        }
        const src = plugin.src;
        try {
            new URL(src);
            plugins.push({...plugin, src});
        } catch {
            plugins.push({...plugin, src: getBrandingPath(src)});
        }
    }
    brand.plugins = plugins;
};

export function useBrandingLoader() {
    const [brand, setBrand] = useState<Manifest>();
    useEffect(() => {
        let ignore = false;
        // TODO: How do we handle missing branding?
        loadBranding()
            .then(brand => {
                if (!ignore) {
                    normalizePlugins(brand);
                    setBrand(brand);
                }
            })
            .catch((error: unknown) => {
                if (!ignore) {
                    setBrand(DEFAULT);
                }
                logger.warn({error}, 'failed to load branding');
            });
        return () => {
            ignore = true;
        };
    }, []);

    useEffect(() => {
        if (brand) {
            try {
                // If backgroundBlurAmountLow is not set, use backgroundBlurAmount
                // to make sure we are not overriding the blur amount for old brands
                // that only have backgroundBlurAmount defined
                if (
                    !brand.applicationConfig?.backgroundBlurAmountLow &&
                    brand.defaultUserConfig?.backgroundBlurAmount
                ) {
                    setApplicationConfig(
                        'backgroundBlurAmountLow',
                        brand.defaultUserConfig.backgroundBlurAmount,
                    );
                }

                setBrandApplicationConfig(brand.applicationConfig);
                setFavicon(brand.favicon);
                setBrandDefaultUserConfig(brand.defaultUserConfig);
                setBrandApplicationTitle(brand.appTitle);
                setBrandPalette(brand.colorPalette);
                setBrandBgColor(brand.backgroundColor);
                setBgReplacementAssets(
                    brand.applicationConfig?.bgImageAssets,
                    brand.defaultUserConfig?.bgImageUrl,
                );
                setLanguages(brand.availableLanguages, brand.translations);

                const relativePath = location.pathname.slice(
                    router.baseUri.length,
                );
                const [pathAlias, pathFlow] =
                    parseMeetingEntryPath(relativePath);

                // always prioritise query params
                const paramsString =
                    location.search.substring(1) || location.hash.substring(3);
                const params = new URLSearchParams(paramsString);
                const flow = params.get('flow') ?? pathFlow;

                if (
                    params.get('mm') === 'true' ||
                    flow === MEETING_MANAGER_PATH
                ) {
                    config.set({
                        key: 'callType',
                        value: toClientCallType('none'),
                    });
                    config.set({key: 'join', value: true});
                }

                if (paramsString) {
                    parseLegacyParams(paramsString, pathAlias, pathFlow);
                }
            } catch (error: unknown) {
                setBrand(DEFAULT);
                logger.error({error}, 'branding manifest is invalid');
            }
        }
    }, [brand]);

    return brand;
}
